import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MailX } from "lucide-react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  DataTable,
  type DataTableColumn,
  EmptyState,
  H2,
  Mono,
  PageHeader,
  Stat,
  Text,
} from "@lagoon/ui";

import { api, type Me, type SiteDetail } from "../api.js";
import { SiteStateBadge } from "../components/badges.js";
import { ReasonAction } from "../components/reason-action.js";

type Release = SiteDetail["releases"][number];

/**
 * One site, and everything an operator can do to it.
 *
 * Every action here demands a reason before it will submit. The database
 * enforces that too, but making the field the only way through the UI means an
 * operator writes the reason while they still remember it, rather than
 * reconstructing it for an auditor later.
 *
 * An auditor sees this screen exactly as an operator does, minus every button.
 * That distinction is the one thing on this screen that must never be softened
 * into a disabled control that submits anyway.
 */
export function SiteScreen({ siteId, me }: { siteId: string; me: Me }): React.JSX.Element {
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ["site", siteId], queryFn: () => api.site(siteId) });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["site", siteId] });
    void queryClient.invalidateQueries({ queryKey: ["health"] });
  }, [queryClient, siteId]);

  const releaseColumns = useReleaseColumns({
    siteId,
    canOperate: me.can.operate,
    publishedDeployId: detail.data?.site.publishedDeployId ?? null,
    onDone: invalidate,
  });

  if (detail.isPending) {
    return (
      <Text size="sm" tone="muted">
        Loading…
      </Text>
    );
  }
  if (detail.error) {
    return (
      <Text size="sm" tone="danger" role="alert">
        {detail.error.message}
      </Text>
    );
  }

  const { site, releases, allowlist, liveSessions } = detail.data;
  const suspended = site.suspendedAt !== null;

  return (
    <div className="lg:flex lg:flex-col lg:gap-8">
      <PageHeader
        breadcrumb={
          <a href="#/" className="lg:text-caption lg:text-muted-foreground lg:hover:underline">
            &larr; Sites and owners
          </a>
        }
        eyebrow="Site"
        title={site.slug}
        description={
          <span>
            {site.name} · owner{" "}
            <a href={`#/owner/${site.ownerId}`} className="lg:text-primary lg:hover:underline">
              <Mono>{site.ownerEmail}</Mono>
            </a>
          </span>
        }
        actions={
          <>
            {site.ownerSuspendedAt !== null && <Badge tone="danger">Owner suspended</Badge>}
            <SiteStateBadge
              suspendedAt={site.suspendedAt}
              publishedDeployId={site.publishedDeployId}
            />
          </>
        }
      />

      {suspended && site.suspendedReason !== null && (
        <Alert tone="danger">
          <AlertTitle>Suspended</AlertTitle>
          <AlertDescription>{site.suspendedReason}</AlertDescription>
        </Alert>
      )}

      <div className="lg:grid lg:grid-cols-2 lg:gap-4 lg:xl:grid-cols-4">
        <Stat value={liveSessions.toLocaleString()} label="Live viewer sessions" />
        <Stat value={allowlist.length.toLocaleString()} label="Allowlisted" />
        <Stat value={releases.length.toLocaleString()} label="Releases" />
        <Stat
          value={new Date(site.createdAt).toLocaleDateString()}
          label="Created"
          className="lg:[&_[data-slot=stat-value]]:text-h3"
        />
      </div>

      {me.can.operate ? (
        <section className="lg:flex lg:flex-col lg:gap-4">
          <H2 className="lg:text-h3">Actions</H2>
          <div className="lg:flex lg:flex-wrap lg:items-start lg:gap-3">
            <ReasonAction
              label={suspended ? "Unsuspend site" : "Suspend site"}
              danger={!suspended}
              hint={
                suspended
                  ? "Visitors can reach the site again on their next request."
                  : "Visitors get 410 on their very next request, signed in or not."
              }
              onSubmit={async (reason) => {
                await (suspended
                  ? api.unsuspendSite(siteId, reason)
                  : api.suspendSite(siteId, reason));
                invalidate();
              }}
            />

            <ReasonAction
              label="Force logout"
              hint={`Ends all ${String(liveSessions)} live viewer sessions. The allowlist is untouched.`}
              onSubmit={async (reason) => {
                await api.forceLogout(siteId, reason);
                invalidate();
              }}
            />

            <ReasonAction
              label="Emergency allowlist removal"
              danger
              field={{
                label: "Email address",
                type: "email",
                placeholder: "viewer@example.com",
                validate: (email) => (email.includes("@") ? null : "Give the address to act on."),
              }}
              hint="Removes the address and revokes their sessions in one transaction."
              onSubmit={async (reason, email) => {
                await api.removeViewer(siteId, email ?? "", reason);
                invalidate();
              }}
            />
          </div>
        </section>
      ) : (
        <Alert tone="info">
          <AlertTitle>Auditor</AlertTitle>
          <AlertDescription>
            You are signed in as an auditor. This view is read-only for your account.
          </AlertDescription>
        </Alert>
      )}

      <section className="lg:flex lg:flex-col lg:gap-4">
        <H2 className="lg:text-h3">Releases</H2>
        <DataTable columns={releaseColumns} data={releases} caption="Releases of this site" />
      </section>

      <section className="lg:flex lg:flex-col lg:gap-4">
        <H2 className="lg:text-h3">Allowlist</H2>
        {allowlist.length === 0 ? (
          <EmptyState
            icon={<MailX />}
            title="Nobody is allowlisted"
            description="Nobody can sign in to this site until an address is added."
            glow={false}
          />
        ) : (
          <ul className="lg:flex lg:flex-wrap lg:gap-2">
            {allowlist.map((entry) => (
              <li key={entry.email}>
                <Badge tone="neutral" className="lg:normal-case lg:font-mono">
                  {entry.email}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Text size="sm">
        <a href={`#/audit?siteId=${site.id}`} className="lg:text-primary lg:hover:underline">
          See every recorded action on this site
        </a>
      </Text>
    </div>
  );
}

/**
 * The release table.
 *
 * The rollback button lives in a column rather than beside the table because
 * an operator picks the release first and the action second, and a control
 * that is not on the row it acts on is how the wrong release gets published.
 */
function useReleaseColumns({
  siteId,
  canOperate,
  publishedDeployId,
  onDone,
}: {
  siteId: string;
  canOperate: boolean;
  publishedDeployId: string | null;
  onDone: () => void;
}): DataTableColumn<Release>[] {
  return useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Release",
        cell: (info) => <Mono className="lg:text-caption">{info.row.original.id.slice(0, 8)}</Mono>,
      },
      {
        accessorKey: "state",
        header: "State",
        cell: (info) => {
          const release = info.row.original;
          const live = release.id === publishedDeployId;
          return (
            <div className="lg:flex lg:flex-col lg:items-start lg:gap-1">
              <div className="lg:flex lg:flex-wrap lg:gap-2">
                <Badge tone={release.state === "failed" ? "danger" : "neutral"}>
                  {release.state}
                </Badge>
                {live && <Badge tone="success">Live</Badge>}
              </div>
              {release.error !== null && (
                <Text size="caption" tone="danger">
                  {release.error}
                </Text>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        sortFn: "datetime",
        cell: (info) => (
          <Mono className="lg:text-caption lg:text-muted-foreground">
            {new Date(info.row.original.createdAt).toLocaleString()}
          </Mono>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: (info) => {
          const release = info.row.original;
          const rollbackable =
            canOperate && release.id !== publishedDeployId && release.state === "ready";
          if (!rollbackable) return null;
          return (
            <ReasonAction
              label="Force rollback"
              danger
              compact
              hint="Repoints the site at this release. Takes effect in seconds."
              onSubmit={async (reason) => {
                await api.forceRollback(siteId, release.id, reason);
                onDone();
              }}
            />
          );
        },
      },
    ],
    [siteId, canOperate, publishedDeployId, onDone],
  );
}
